@if (isStoreLoading()) {
  <section class="sav-store-skeleton" aria-label="Loading store details">
    <div class="sav-store-skeleton-shell">
      <aside class="sav-store-skeleton-sidebar">
        <span class="sav-skeleton sav-store-skeleton-logo"></span>
        <span class="sav-skeleton sav-store-skeleton-pill"></span>
        <span class="sav-skeleton sav-store-skeleton-button"></span>
        <span class="sav-skeleton sav-store-skeleton-card"></span>
        <span class="sav-skeleton sav-store-skeleton-card"></span>
      </aside>

      <div class="sav-store-skeleton-main">
        <span class="sav-skeleton sav-store-skeleton-title"></span>
        <span class="sav-skeleton sav-store-skeleton-subtitle"></span>
        <span class="sav-skeleton sav-store-skeleton-offer"></span>
        <span class="sav-skeleton sav-store-skeleton-offer"></span>
        <span class="sav-skeleton sav-store-skeleton-offer"></span>
      </div>
    </div>
  </section>
} @else if (store()) {
  <section class="sav-top" aria-label="Store top offers hero">
    <div class="sav-top-shell">
      <div class="sav-top-wrap">
        <!--
          The sidebar is FIRST in the document, even though on desktop the grid puts it in column 1
          and on mobile `order` decides where it lands. Source order is not what positions it —
          `grid-column` and `order` do — but it is what decides the order the browser PARSES it in,
          and that is a layout-shift problem: the sidebar renders above the offer list on mobile, so
          while it sat second in the source the list painted first and was then pushed 66px down when
          the logo badge arrived. Measured at 0.074 of the page's CLS, from a single shift.
        -->
        <aside class="sav-left-col">
          <!--
            The logo is a click-out to the merchant, like every other CTA on the page — and it goes
            through the same `/go/` redirect they do, so the merchant URL is not sitting in
            this page's source and the click is counted. `sponsored` because that is what an
            affiliate link is, and `noopener` because the new tab must not get a handle back to this
            window.

            An anchor only when there is somewhere to go: a link to nowhere is worse than plain
            artwork. The inner content lives in a template so the two branches cannot drift apart.
          -->
          <ng-template #logoArtwork>
            <!--
              The shared logo slot: the image, or the whole store name on a white ground when there
              is none or it fails to load. This hero is where that treatment was born; it now lives
              in app-store-logo so every other slot on the site paints the same thing. Eager, since
              the badge sits in the first viewport and is often the page's LCP.
            -->
            <app-store-logo
              [logoUrl]="storeLogoUrl()"
              [name]="storeDisplayName()"
              loading="eager"
            />
            <!--
              The running event's name across the foot of the logo ("Halloween Sale"), so a shopper
              sees at a glance that this store is in the sale. Only for an event the store has a live
              offer in (see eventRibbon); the pumpkins are Halloween's alone.
            -->
            @if (eventRibbon(); as ribbon) {
              <span class="sav-event-ribbon" [attr.data-theme]="ribbon.theme">
                @if (ribbon.theme === 'halloween') {
                  <ng-container [ngTemplateOutlet]="ribbonPumpkin" />
                }
                <span class="sav-event-ribbon-text">{{ ribbon.name }}</span>
                @if (ribbon.theme === 'halloween') {
                  <ng-container [ngTemplateOutlet]="ribbonPumpkin" />
                }
              </span>
            }
          </ng-template>

          <ng-template #ribbonPumpkin>
            <svg class="sav-event-ribbon-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 7c-.6-2 .2-3.6 1.8-4.4" fill="none" stroke="#3f7d20" stroke-width="2" stroke-linecap="round" />
              <ellipse cx="7.6" cy="14.5" rx="5.4" ry="6.8" fill="#ea580c" />
              <ellipse cx="16.4" cy="14.5" rx="5.4" ry="6.8" fill="#ea580c" />
              <ellipse cx="12" cy="14.5" rx="4.6" ry="7.4" fill="#fb923c" />
            </svg>
          </ng-template>

          @if (storeGoUrl(); as merchantUrl) {
            <a
              class="sav-logo-badge sav-logo-badge-link"
              [class.has-event-ribbon]="eventRibbon() !== null"
              [href]="merchantUrl"
              target="_blank"
              rel="nofollow sponsored noopener"
              [attr.aria-label]="'Visit ' + storeDisplayName() + (eventRibbon() ? ', ' + eventRibbon()?.name : '')"
            >
              <ng-container [ngTemplateOutlet]="logoArtwork" />
            </a>
          } @else {
            <div
              class="sav-logo-badge"
              [class.has-event-ribbon]="eventRibbon() !== null"
              [attr.aria-label]="storeDisplayName() + ' logo' + (eventRibbon() ? ', ' + eventRibbon()?.name : '')"
            >
              <ng-container [ngTemplateOutlet]="logoArtwork" />
            </div>
          }

          <app-breadcrumb [items]="crumbs()" />

          <div class="sav-rating" aria-label="Store rating">
            <div class="sav-rating-stars" role="group" aria-label="Rate this store">
              @for (star of ratingStars; track star) {
                <button
                  type="button"
                  class="sav-rating-star"
                  [class.is-active]="star <= activeStars()"
                  [disabled]="hasRated() || submittingRating()"
                  (mouseenter)="setHoverRating(star)"
                  (mouseleave)="clearHoverRating()"
                  (focus)="setHoverRating(star)"
                  (blur)="clearHoverRating()"
                  (click)="submitRating(star)"
                  [attr.aria-label]="star + (star === 1 ? ' star' : ' stars')"
                >
                  <svg
                    width="18px"
                    height="17px"
                    viewBox="0 0 18 17"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                      <g transform="translate(-273.000000, -4323.000000)">
                        <g transform="translate(100.000000, 4266.000000)">
                          <g transform="translate(170.000000, 54.000000)">
                            <g>
                              <polygon fill-rule="nonzero" points="0 0 24 0 24 24 0 24"></polygon>
                              <path
                                d="M12,17.27 L16.15,19.78 C16.91,20.24 17.84,19.56 17.64,18.7 L16.54,13.98 L20.21,10.8 C20.88,10.22 20.52,9.12 19.64,9.05 L14.81,8.64 L12.92,4.18 C12.58,3.37 11.42,3.37 11.08,4.18 L9.19,8.63 L4.36,9.04 C3.48,9.11 3.12,10.21 3.79,10.79 L7.46,13.97 L6.36,18.69 C6.16,19.55 7.09,20.23 7.85,19.77 L12,17.27 Z"
                                [attr.fill]="star <= activeStars() ? '#F5B61A' : '#D9D9D9'"
                              ></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </button>
              }
            </div>

            <span class="sav-rating-text">
              @if (ratingCount() > 0) {
                {{ ratingAverage() }} ({{ ratingCount() }}
                {{ ratingCount() === 1 ? 'rating' : 'ratings' }})
              } @else {
                No ratings yet
              }
            </span>

            @if (submittingRating()) {
              <span class="sav-rating-note">Submitting…</span>
            } @else if (justRated()) {
              <span class="sav-rating-note is-success">Thanks for rating!</span>
            } @else if (hasRated()) {
              <span class="sav-rating-note">You rated this store</span>
            } @else if (ratingError()) {
              <span class="sav-rating-note is-error">{{ ratingError() }}</span>
            } @else {
              <span class="sav-rating-note">Click a star to rate</span>
            }
          </div>

          <!--
            One row, and styled as text rather than as buttons. They stay <button> elements because
            each one does something — follow, share, open the review form — and a screen reader has
            to be told that; what changed is only how they look.
          -->
          <div class="sav-sidebar-actions" aria-label="Store actions">
            <button type="button" (click)="toggleFollow()" [disabled]="followed()">
              {{ followed() ? 'Following' : 'Follow' }}
              <span class="sav-action-count">{{ formatCount(followerCount()) }}</span>
            </button>
            <button type="button" (click)="shareStore()">
              {{ shareLabel() }}
              <span class="sav-action-count">{{ formatCount(shareCount()) }}</span>
            </button>
            <button type="button" (click)="toggleReview()">Leave a Review</button>
          </div>

          <!-- Cross-link to the product side of this store. Hidden unless that page exists. -->
          @if (dealsPagePath()) {
            <a class="sav-cross-page-link" [routerLink]="dealsPagePath()">
              View {{ storeDisplayName() }} All Deals
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </a>
          }

          @if (followed()) {
            <p class="sav-follow-done">You're following this store — we'll email you new deals.</p>
          } @else if (isFollowOpen()) {
            <form
              class="sav-follow-form"
              [formGroup]="followForm"
              (ngSubmit)="submitFollow()"
              novalidate
            >
              <div class="sav-honeypot" aria-hidden="true">
                <label
                  >Company website<input
                    formControlName="companyWebsite"
                    type="text"
                    tabindex="-1"
                    autocomplete="off"
                /></label>
              </div>
              <label class="sav-follow-label" for="follow-email">Get deal alerts by email</label>
              <div class="sav-follow-row">
                <input
                  id="follow-email"
                  formControlName="email"
                  type="email"
                  placeholder="you@example.com"
                />
                <button type="submit" [disabled]="followSubmitting()">
                  {{ followSubmitting() ? '…' : 'Follow' }}
                </button>
              </div>
              @if (followForm.controls.email.invalid && followForm.controls.email.touched) {
                <span class="sav-follow-error">Enter a valid email address.</span>
              } @else if (followError()) {
                <span class="sav-follow-error">{{ followError() }}</span>
              }
            </form>
          }

          <p>
            When you buy through links on SavingArena
            <a href="#">we may earn a commission.</a>
          </p>

          <section class="sav-sidebar-panels" aria-label="Store trust and updates">
            @if (pageEditor(); as editor) {
              <article class="sav-side-card sav-side-card-updated">
                <p class="sav-side-card-title">Reviewed by</p>
                <div class="sav-author-row">
                  @if (editor.avatarUrl) {
                    <img
                      class="sav-author-avatar"
                      [src]="editor.avatarUrl"
                      [alt]="editor.name"
                      width="96"
                      height="96"
                    />
                  } @else {
                    <span class="sav-author-avatar sav-author-avatar-initials" aria-hidden="true">{{
                      editor.initials
                    }}</span>
                  }
                  <div class="sav-author-meta">
                    <p class="sav-author-name">{{ editor.name }}</p>
                    @if (editor.role) {
                      <p class="sav-author-role">{{ editor.role }}</p>
                    }
                  </div>
                </div>
                @if (editor.bio) {
                  <p class="sav-author-bio">{{ editor.bio }}</p>
                }
                @if (editor.profileUrl) {
                    <!-- Names the destination: "Read more" alone is not descriptive link text, and the audit
                         reads the visible words rather than any aria-label. -->
                  <a class="sav-author-bio-link" [routerLink]="editor.profileUrl"
                    >Read more about {{ editor.name }}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                      ></path>
                    </svg>
                  </a>
                }
              </article>
            }

            <article class="sav-side-card sav-side-card-trust">
              <p class="sav-side-card-title">Why Trust Us?</p>
              <p>
                We validate coupon accuracy, remove non-working offers, and prioritize verified
                discounts for a cleaner shopping experience.
              </p>
              <!-- The words themselves have to say where the link goes. "See More" did not, and an
                   `aria-label` is no substitute: it is the visible text a search engine reads, and
                   the text a person scanning the page reads too. -->
              <a routerLink="/why-trust-us"
                >See our standards
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                  ></path>
                </svg>
              </a>
            </article>

            <article class="sav-side-card sav-side-card-verify">
              <p class="sav-side-card-title">How Verification Works</p>
              <p>
                We test codes, check terms, and monitor expirations. Verified offers are updated
                continuously to keep this page reliable.
              </p>
              <a routerLink="/how-verification-works"
                >See how we verify
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                  ></path>
                </svg>
              </a>
            </article>

            <article class="sav-side-card sav-side-card-about">
              <p class="sav-side-card-title">{{ aboutStoreHeading }}</p>
              <!-- The text is printed in full and the clamp is purely visual, so the whole of it is
                   in the server-rendered HTML. It used to be sliced to 300 characters until someone
                   clicked, which hid the rest from crawlers. -->
              <input
                type="checkbox"
                class="sav-readmore-input"
                id="sav-about-readmore"
                aria-controls="sav-about-readmore-text"
                aria-label="Show the full store description"
              />
              <p
                id="sav-about-readmore-text"
                class="sav-readmore-text"
                [class.is-clamped]="shouldShowAboutToggle"
              >
                {{ aboutStoreText }}
              </p>
              @if (shouldShowAboutToggle) {
                <label class="sav-about-toggle" for="sav-about-readmore">
                  <span class="sav-readmore-more">Read More</span>
                  <span class="sav-readmore-less">Read Less</span>
                  <span class="sav-about-toggle-icon" aria-hidden="true">+</span>
                </label>
              }
            </article>

            @for (widget of sidebarWidgetItems(); track widget.id) {
              <article class="sav-side-card sav-side-card-about">
                <p class="sav-side-card-title">{{ widget.title }}</p>
                <input
                  type="checkbox"
                  class="sav-readmore-input"
                  [id]="'sav-widget-readmore-' + widget.id"
                  [attr.aria-controls]="'sav-widget-readmore-' + widget.id + '-text'"
                  [attr.aria-label]="'Show the full text of ' + widget.title"
                />
                <p
                  [id]="'sav-widget-readmore-' + widget.id + '-text'"
                  class="sav-readmore-text"
                  [class.is-clamped]="shouldShowSidebarWidgetToggle(widget)"
                >
                  {{ widget.text }}
                </p>
                @if (shouldShowSidebarWidgetToggle(widget)) {
                  <label class="sav-about-toggle" [attr.for]="'sav-widget-readmore-' + widget.id">
                    <span class="sav-readmore-more">Read More</span>
                    <span class="sav-readmore-less">Read Less</span>
                    <span class="sav-about-toggle-icon" aria-hidden="true">+</span>
                  </label>
                }
              </article>
            }

            @if (storeVideoId()) {
              <article class="sav-side-card sav-video-card" aria-label="Store video">
                <p class="sav-side-card-title">Watch &amp; Save</p>
                <div class="sav-video-frame">
                  @if (isVideoActivated()) {
                    <iframe
                      class="sav-video-iframe"
                      [src]="storeVideoEmbedUrl()"
                      [title]="storeDisplayName() + ' video'"
                      allow="
                        accelerometer;
                        autoplay;
                        clipboard-write;
                        encrypted-media;
                        gyroscope;
                        picture-in-picture;
                        web-share;
                      "
                      referrerpolicy="strict-origin-when-cross-origin"
                      allowfullscreen
                    ></iframe>
                  } @else {
                    <button
                      type="button"
                      class="sav-video-facade"
                      [style.background-image]="'url(' + storeVideoThumbUrl() + ')'"
                      [attr.aria-label]="'Play ' + storeDisplayName() + ' video'"
                      (click)="activateVideo()"
                    >
                      <span class="sav-video-play" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"></path>
                        </svg>
                      </span>
                    </button>
                  }
                </div>
              </article>
            }

            <article class="sav-submit-coupon" aria-label="Submit coupon callout">
              <div class="sav-submit-coupon-content">
                <div class="sav-submit-coupon-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 14L16 10L12 6"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                    <path
                      d="M4 20V10C4 8.89543 4.89543 8 6 8H18"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                  Community Reward
                </div>

                <p class="sav-side-card-title">Found a better deal?</p>

                <p>
                  Share your <strong>{{ storeDisplayName() }}</strong> coupon code with the
                  community and help others save big today.
                </p>

                <!--
                  The store travels in router state, not in the URL.

                  This used to be `?store=<slug>`, which gave the one submit-coupon page a distinct
                  address for every store on the site — 36,675 of them. A crawl found them all: they
                  were 46% of every URL on savingarena.com, one page reached 36,675 ways. The
                  canonical kept them out of the index, but a crawler still has to fetch each one
                  before it can read that canonical, so nearly half the crawl budget went on a form.

                  State carries the same information without minting a URL for it, and it carries
                  more of it: the name and logo come along too, so the form fills in immediately
                  instead of looking the store up again by slug.
                -->
                <a
                  routerLink="/submit-coupon"
                  [state]="{
                    submitCouponStore: {
                      slug: store()?.slug ?? '',
                      name: storeDisplayName(),
                      logoImagePath: store()?.logoImagePath ?? '',
                    },
                  }"
                  class="sav-submit-coupon-button"
                  >Submit coupon
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <line
                      x1="5"
                      y1="12"
                      x2="19"
                      y2="12"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    ></line>
                    <polyline
                      points="12 5 19 12 12 19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></polyline>
                  </svg>
                </a>
              </div>
            </article>

            @if (hasCustomerCareSection()) {
              <article class="sav-customer-care" aria-label="Customer care">
                @if (supportLinkItems().length > 0) {
                  <div class="sav-customer-care-block">
                    <p class="sav-side-card-title">Customer Care</p>

                    <nav>
                      @for (link of supportLinkItems(); track link.id) {
                        <a
                          [href]="link.url"
                          class="sav-care-link"
                          [attr.target]="getExternalTarget(link.url)"
                          [attr.rel]="getExternalRel(link.url)"
                        >
                          <span class="sav-care-link-icon" aria-hidden="true">
                            @switch (link.icon) {
                              @case ('shipping') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M20 7L12 3L4 7M20 7L12 11M20 7V17L12 21M12 11L4 7M12 11V21M4 7V17L12 21"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></path>
                                </svg>
                              }
                              @case ('returns') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M16 15V14C16 11.79 14.21 10 12 10H8M8 10L11 13M8 10L11 7M20 20V6C20 4.9 19.1 4 18 4H6C4.9 4 4 4.9 4 6V20L8 18L12 20L16 18L20 20Z"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></path>
                                </svg>
                              }
                              @case ('payment') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M9 12L11 14L15 10M20.62 5.98C17.03 5.76 14.1 4.66 12 2.94C9.9 4.66 6.97 5.76 3.38 5.98C3.13 6.94 3 7.95 3 9C3 14.59 6.82 19.29 12 20.62C17.18 19.29 21 14.59 21 9C21 7.95 20.87 6.94 20.62 5.98Z"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></path>
                                </svg>
                              }
                              @default {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12 3L20 7V12C20 16.5 16.8 20.4 12 21C7.2 20.4 4 16.5 4 12V7L12 3Z"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></path>
                                </svg>
                              }
                            }
                          </span>
                          <span>
                            <strong>{{ link.title }}</strong>
                            @if (link.description) {
                              <small>{{ link.description }}</small>
                            }
                          </span>
                        </a>
                      }
                    </nav>
                  </div>
                }

                @if (hasStoreContactSection()) {
                  <div class="sav-customer-care-contact">
                    <p class="sav-side-card-title">Store Contact Details</p>

                    <ul>
                      @if (contactPhone(); as phone) {
                        <li>
                          <span class="sav-contact-item-icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M21 16.5V19.5C21 20.05 20.55 20.5 20 20.5C11.44 20.5 4.5 13.56 4.5 5C4.5 4.45 4.95 4 5.5 4H8.5C9.05 4 9.5 4.45 9.5 5V7.75C9.5 8.3 9.05 8.75 8.5 8.75H7.45C8.02 11.56 10.19 13.73 13 14.3V13.25C13 12.7 13.45 12.25 14 12.25H16.75C17.3 12.25 17.75 12.7 17.75 13.25V16C17.75 16.55 17.3 17 16.75 17H14.5"
                                stroke="currentColor"
                                stroke-width="1.7"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ></path>
                            </svg>
                          </span>
                          <span class="sav-contact-text">
                            <strong class="sav-contact-label">Phone Number</strong>
                            <a [href]="contactPhoneHref()" class="sav-contact-value">{{ phone }}</a>
                          </span>
                        </li>
                      }
                      @if (contactAddress(); as address) {
                        <li>
                          <span class="sav-contact-item-icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M20 10C20 15 12 21 12 21C12 21 4 15 4 10C4 5.58 7.58 2 12 2C16.42 2 20 5.58 20 10Z"
                                stroke="currentColor"
                                stroke-width="1.7"
                              ></path>
                              <circle
                                cx="12"
                                cy="10"
                                r="3"
                                stroke="currentColor"
                                stroke-width="1.7"
                              ></circle>
                            </svg>
                          </span>
                          <span class="sav-contact-text">
                            <strong class="sav-contact-label">Address</strong>
                            <span class="sav-contact-value">{{ address }}</span>
                          </span>
                        </li>
                      }
                      @if (contactEmail(); as email) {
                        <li>
                          <span class="sav-contact-item-icon" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                              <rect
                                x="4"
                                y="6"
                                width="16"
                                height="12"
                                stroke="currentColor"
                                stroke-width="1.7"
                              ></rect>
                              <path
                                d="M4 7L12 13L20 7"
                                stroke="currentColor"
                                stroke-width="1.7"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ></path>
                            </svg>
                          </span>
                          <span class="sav-contact-text">
                            <strong class="sav-contact-label">Email</strong>
                            <a [href]="contactEmailHref()" class="sav-contact-value">{{ email }}</a>
                          </span>
                        </li>
                      }
                    </ul>

                    @if (socialLinkItems().length > 0) {
                      <div class="sav-customer-care-social" aria-label="Store social media links">
                        @for (link of socialLinkItems(); track link.id) {
                          <a
                            [href]="link.url"
                            [attr.aria-label]="link.label"
                            [attr.target]="getExternalTarget(link.url)"
                            [attr.rel]="getExternalRel(link.url)"
                          >
                            @switch (link.icon) {
                              @case ('facebook') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.08 5.66 21.24 10.44 22V14.97H7.9V12.06H10.44V9.85C10.44 7.33 11.93 5.94 14.22 5.94C15.32 5.94 16.48 6.14 16.48 6.14V8.61H15.21C13.96 8.61 13.56 9.39 13.56 10.19V12.06H16.36L15.91 14.97H13.56V22C18.34 21.24 22 17.08 22 12.06Z"
                                  ></path>
                                </svg>
                              }
                              @case ('instagram') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <rect
                                    x="3"
                                    y="3"
                                    width="18"
                                    height="18"
                                    rx="5"
                                    stroke="currentColor"
                                    stroke-width="2"
                                  ></rect>
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="4"
                                    stroke="currentColor"
                                    stroke-width="2"
                                  ></circle>
                                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor"></circle>
                                </svg>
                              }
                              @case ('x') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M18.9 2H21.98L15.25 9.69L23.17 22H16.95L12.08 14.55L5.57 22H2.47L9.67 13.77L2.08 2H8.46L12.86 8.88L18.9 2ZM17.82 20.14H19.51L7.54 3.76H5.73L17.82 20.14Z"
                                  ></path>
                                </svg>
                              }
                              @case ('youtube') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M21.58 7.19C21.35 6.33 20.68 5.65 19.82 5.42C18.26 5 12 5 12 5S5.74 5 4.18 5.42C3.32 5.65 2.65 6.33 2.42 7.19C2 8.75 2 12 2 12S2 15.25 2.42 16.81C2.65 17.67 3.32 18.35 4.18 18.58C5.74 19 12 19 12 19S18.26 19 19.82 18.58C20.68 18.35 21.35 17.67 21.58 16.81C22 15.25 22 12 22 12S22 8.75 21.58 7.19ZM10 15V9L15.2 12L10 15Z"
                                  ></path>
                                </svg>
                              }
                              @case ('linkedin') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M6.94 8.9H3.7V20H6.94V8.9ZM5.32 4C4.28 4 3.45 4.84 3.45 5.87C3.45 6.91 4.28 7.74 5.32 7.74C6.35 7.74 7.19 6.91 7.19 5.87C7.19 4.84 6.35 4 5.32 4ZM20.55 13.64C20.55 10.66 18.96 8.62 16.42 8.62C14.72 8.62 13.96 9.56 13.51 10.22V8.9H10.4C10.44 9.78 10.4 20 10.4 20H13.64V13.8C13.64 13.47 13.66 13.14 13.76 12.9C14 12.24 14.55 11.56 15.47 11.56C16.68 11.56 17.16 12.48 17.16 13.83V20H20.4L20.55 13.64Z"
                                  ></path>
                                </svg>
                              }
                              @case ('pinterest') {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M12.04 2C6.73 2 3 5.67 3 10.59C3 14.29 5.08 16.4 6.41 16.4C7.03 16.4 7.4 14.69 7.4 14.19C7.4 13.59 5.91 12.33 5.91 10.23C5.91 6.8 8.52 4.37 11.9 4.37C14.8 4.37 16.95 6 16.95 8.99C16.95 11.22 16.05 14.75 13.15 14.75C12.1 14.75 11.26 13.97 11.26 12.89C11.26 11.3 12.39 9.75 12.39 8.09C12.39 5.26 8.22 6.79 8.22 10.96C8.22 11.76 8.33 12.65 8.76 13.34C8.23 15.62 7.16 19.03 7.16 21.39C7.16 22.11 7.27 22.82 7.35 23.54C7.5 23.71 7.42 23.69 7.67 23.46C10.11 20.29 10.03 19.67 11.13 15.56C11.72 16.69 13.28 17.31 14.51 17.31C18.7 17.31 20.58 13.26 20.58 9.52C20.58 4.84 16.56 2 12.04 2Z"
                                  ></path>
                                </svg>
                              }
                              @default {
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M10 13C10.43 13.57 11.07 14 12 14C13.1 14 14.1 13.45 14.71 12.59L17.71 8.59C18.68 7.3 18.42 5.43 17.12 4.42C15.82 3.42 13.94 3.65 12.93 4.93L12 6.11M14 11C13.57 10.43 12.93 10 12 10C10.9 10 9.9 10.55 9.29 11.41L6.29 15.41C5.32 16.7 5.58 18.57 6.88 19.58C8.18 20.58 10.06 20.35 11.07 19.07L12 17.89"
                                    stroke="currentColor"
                                    stroke-width="1.8"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  ></path>
                                </svg>
                              }
                            }
                          </a>
                        }
                      </div>
                    }
                  </div>
                }
              </article>
            }
          </section>
        </aside>

        <div class="sav-right-col">
          <!-- `--sav-h1-fit` carries how long this heading is, so the desktop ramp can size it to
               one line rather than guessing from the viewport. See `storeHeadingFit`. -->
          <h1 [style.--sav-h1-fit]="storeHeadingFit()">
            {{ storeHeading() }}
            <!-- `role="img"` is what makes the label legal here: `aria-label` is only honoured on
                 an element that has a role, and on a bare <span> it was ignored — so the tick beside
                 the heading was announced as nothing at all. -->
            <span
              class="sav-h1-verified"
              role="img"
              aria-label="Verified store"
              title="Verified store"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M12 1.75C13.34 1.75 14.44 2.71 14.77 3.99C16.07 3.67 17.43 4.19 18.19 5.28C19.26 6.05 19.75 7.41 19.43 8.71C20.71 9.04 21.67 10.14 21.67 11.48C21.67 12.82 20.71 13.92 19.43 14.25C19.75 15.55 19.26 16.91 18.19 17.68C17.43 18.77 16.07 19.29 14.77 18.97C14.44 20.25 13.34 21.21 12 21.21C10.66 21.21 9.56 20.25 9.23 18.97C7.93 19.29 6.57 18.77 5.81 17.68C4.74 16.91 4.25 15.55 4.57 14.25C3.29 13.92 2.33 12.82 2.33 11.48C2.33 10.14 3.29 9.04 4.57 8.71C4.25 7.41 4.74 6.05 5.81 5.28C6.57 4.19 7.93 3.67 9.23 3.99C9.56 2.71 10.66 1.75 12 1.75Z"
                ></path>
                <path
                  d="M10.76 14.86L7.86 11.95L9.27 10.54L10.76 12.03L14.73 8.06L16.14 9.47L10.76 14.86Z"
                  fill="#ffffff"
                ></path>
              </svg>
            </span>
          </h1>
          @if (storeSubheading(); as subheading) {
            <!--
              The admin's own H2 (Stores > Content > H2 Heading). It sits directly under the H1
              because that is what it is written to be — a second line on the title, not a heading
              for any one section further down.
            -->
            <h2 class="sav-store-subheading">{{ subheading }}</h2>
          }

          <!--
            A heading a screen reader announces but a sighted reader never sees.
          
            The offer list deliberately has no visible heading — a label a screen reader can announce
            does not have to be something a sighted reader must read past. But the coupon cards below
            are h3, and when a store has no subheading the h2 above never renders, so the document
            jumped h1 -> h3 and failed the heading-order audit. This fills that level without putting
            anything on screen.
          -->
          <h2 class="sr-only">{{ activeOffersSubtitle() }}</h2>
          @if (topCoupon(); as coupon) {
            <app-coupon-card [card]="coupon" variant="featured" (action)="openOffer($event)" />
          }

          @if (couponList().length > 0) {
            <section class="sav-offer-list" [attr.aria-label]="activeOffersSubtitle()">
              @for (coupon of couponList(); track coupon.id) {
                <app-coupon-card [card]="coupon" (action)="openOffer($event)" />
              }
            </section>
          }

          @if (hasMoreCoupons()) {
            <div class="sav-load-more-wrap">
              <button
                type="button"
                class="sav-load-more-btn"
                [disabled]="loadingMoreCoupons()"
                (click)="loadMoreCoupons()"
              >
                @if (loadingMoreCoupons()) {
                  Loading…
                } @else {
                  Load more coupons
                  <span class="sav-load-more-count"
                    >({{ couponCards().length }} of {{ activeCouponsCount() }})</span
                  >
                }
              </button>
            </div>
          }

          @if (expiredCouponCards().length > 0) {
            <section class="sav-expired-section" aria-label="Expired coupons">
              <app-section-heading
                heading="Expired Coupons"
                subtitle="Past their end date, but often still working — worth a try at checkout."
              />

              <div class="sav-offer-list">
                @for (coupon of expiredCouponCards(); track coupon.id) {
                  <app-coupon-card [card]="coupon" variant="expired" (action)="openOffer($event)" />
                }
              </div>
            </section>
          }
        </div>


        @if (competitorStores().length > 0) {
          <section class="sav-competitors" [attr.aria-label]="storeDisplayName() + ' competitors'">
            <app-section-heading
              [heading]="storeDisplayName() + ' Competitors'"
              [subtitle]="'Other stores selling the same kind of thing, worth a price check before you buy.'"
            />

            <div class="sav-competitor-grid">
              @for (competitor of competitorStores(); track competitor.id) {
                <!--
                  No aria-label. It read "<name> competitor" while the link itself shows the name, the
                  domain and the offer summary, so the accessible name did not contain the visible text and
                  the two disagreed. The card's own content is both a better name and a matching one.
                -->
                <a class="sav-competitor-card" [routerLink]="competitor.path">
                  <!-- Decorative: the heading beside it carries the name. -->
                  <app-store-logo
                    [logoUrl]="competitor.logoUrl"
                    [name]="competitor.name"
                    decorative
                  />
                  <div class="sav-competitor-content">
                    <h3>
                      {{ competitor.name }}
                      <span class="sav-competitor-verified" aria-hidden="true">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M12 1.75C13.34 1.75 14.44 2.71 14.77 3.99C16.07 3.67 17.43 4.19 18.19 5.28C19.26 6.05 19.75 7.41 19.43 8.71C20.71 9.04 21.67 10.14 21.67 11.48C21.67 12.82 20.71 13.92 19.43 14.25C19.75 15.55 19.26 16.91 18.19 17.68C17.43 18.77 16.07 19.29 14.77 18.97C14.44 20.25 13.34 21.21 12 21.21C10.66 21.21 9.56 20.25 9.23 18.97C7.93 19.29 6.57 18.77 5.81 17.68C4.74 16.91 4.25 15.55 4.57 14.25C3.29 13.92 2.33 12.82 2.33 11.48C2.33 10.14 3.29 9.04 4.57 8.71C4.25 7.41 4.74 6.05 5.81 5.28C6.57 4.19 7.93 3.67 9.23 3.99C9.56 2.71 10.66 1.75 12 1.75Z"
                          ></path>
                          <path
                            d="M10.76 14.86L7.86 11.95L9.27 10.54L10.76 12.03L14.73 8.06L16.14 9.47L10.76 14.86Z"
                            fill="#ffffff"
                          ></path>
                        </svg>
                      </span>
                    </h3>
                    @if (competitor.domain) {
                      <p class="sav-competitor-domain">{{ competitor.domain }}</p>
                    }
                    <!-- What the store offers, written from its own figures — the card used to
                         carry a paragraph of prose that read the same on every store. -->
                    <p class="sav-competitor-summary">{{ competitor.summary }}</p>
                  </div>
                </a>
              }
            </div>
          </section>
        }

        <!--
          Compare Similar Stores.

          Cards are only rendered for stores that have a PUBLISHED comparison with this store — the
          "View comparison" button must always land on a real page. Stores without one are simply not
          shown, rather than rendering a button that goes nowhere (which is what this section did
          before: three hardcoded cards with dead <button>s).
        -->
        <!--
          This store's discount pages (Student / Teacher / …). Rendered only for pages that are
          actually published, so every link lands on a real page; each of those pages links back
          here in turn.
        -->
        @if (discountPageLinks().length > 0) {
          <section class="sav-store-discounts" aria-label="Discount pages for this store">
            <app-section-heading
              [heading]="storeDisplayName() + ' discount pages'"
              subtitle="Eligibility, verification and the offers we list for each group."
            />
            <div class="sav-store-discounts-grid">
              @for (link of discountPageLinks(); track link.slug) {
                <a
                  class="sav-store-discount-card"
                  [routerLink]="'/' + link.slug"
                  [attr.aria-label]="storeDisplayName() + ' ' + link.discountTypeLabel"
                >
                  <span class="sav-store-discount-name">{{ link.discountTypeLabel }}</span>
                  <span class="sav-store-discount-go" aria-hidden="true">→</span>
                </a>
              }
            </div>
          </section>
        }

        @if (comparisonCards().length > 0) {
          <section class="sav-compare" aria-label="Compare similar stores">
            <header class="sav-compare-head">
              <div>
                <h2>Compare Similar Stores</h2>
                <p>
                  Quickly compare coupon availability, typical discounts, shipping promos, and deal
                  reliability across similar stores.
                </p>
              </div>
            </header>

            <div class="sav-compare-grid">
              @for (card of comparisonCards(); track card.comparisonPath) {
                <article class="sav-compare-card">
                  @if (card.categoryName) {
                    <span class="sav-compare-card-eyebrow">{{ card.categoryName }}</span>
                  }

                  <!-- Head-to-head: this store on the left, the rival on the right, with each
                       side's live offer count. The winning side is emphasised only when the API
                       reports a winner — a tie leaves both sides level. -->
                  <div class="sav-compare-versus">
                    <div class="sav-compare-side" [class.is-winner]="card.selfWins">
                      <span class="sav-compare-side-logo">
                        <app-store-logo [logoUrl]="card.selfLogoUrl" [name]="card.selfName" decorative />
                      </span>
                      <span class="sav-compare-side-name">{{ card.selfName }}</span>
                      @if (card.selfOffers !== null) {
                        <span class="sav-compare-side-stat"
                          ><strong>{{ card.selfOffers }}</strong> offers</span
                        >
                      }
                    </div>

                    <span class="sav-compare-vs" aria-hidden="true">VS</span>

                    <div class="sav-compare-side" [class.is-winner]="card.winnerName === card.name">
                      <span class="sav-compare-side-logo">
                        <app-store-logo [logoUrl]="card.logoUrl" [name]="card.name" decorative />
                      </span>
                      <span class="sav-compare-side-name">{{ card.name }}</span>
                      @if (card.offers !== null) {
                        <span class="sav-compare-side-stat"
                          ><strong>{{ card.offers }}</strong> offers</span
                        >
                      }
                    </div>
                  </div>

                  @if (card.winnerName) {
                    <p class="sav-compare-verdict">
                      <span class="sav-compare-verdict-badge">{{ card.winnerName }}</span> leads on
                      offers
                    </p>
                  }

                  <div class="sav-compare-card-actions">
                    <a class="is-primary" [routerLink]="card.comparisonPath"
                      >Compare
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M5 12h13m-5-6l6 6-6 6"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </a>
                    <a class="sav-compare-view-store" [routerLink]="card.storePath"
                      >View {{ card.name }}</a
                    >
                  </div>
                </article>
              }
            </div>
          </section>
        }

        <!-- Latest stores in this store's primary category, with real offer counts. Hidden when
             the category has none to show. -->
        @if (popularStores().length > 0) {
          <section class="sav-trending-stores-section" [attr.aria-label]="popularStoresHeading()">
            <div class="sav-trending-stores-wrap">
              <app-section-heading
                [heading]="popularStoresHeading()"
                subtitle="The most active stores in this category right now."
              />

              <div class="sav-trending-stores-grid">
                @for (item of popularStores(); track item.id) {
                  <a
                    class="sav-trending-store-card"
                    [routerLink]="item.path || null"
                  >
                    <span class="sav-store-tile-logo-box">
                      <app-store-logo [logoUrl]="item.logoUrl" [name]="item.name" decorative />
                    </span>
                    <span class="sav-store-tile-divider" aria-hidden="true"></span>
                    <span class="sav-store-tile-store-meta">
                      <span class="sav-store-tile-brand-name">{{ item.name }}</span>
                      @if (item.countLabel) {
                        <span class="sav-store-tile-count">{{ item.countLabel }}</span>
                      }
                    </span>
                  </a>
                }
              </div>
            </div>
          </section>
        }

        @if (internalLinkStoreItems().length > 0) {
          <section
            class="sav-trending-stores-section"
            aria-label="Top picks from our editorial team"
          >
            <div class="sav-trending-stores-wrap">
              <app-section-heading
                heading="Top Pick From Our Editorial Team"
                subtitle="Stores our editors rate for reliable discounts and clean checkouts."
              />

              <div class="sav-trending-stores-grid">
                @for (store of internalLinkStoreItems(); track store.id) {
                  <a
                    class="sav-trending-store-card"
                    [routerLink]="store.path"
                  >
                    <span class="sav-store-tile-logo-box">
                      <app-store-logo [logoUrl]="store.logoUrl" [name]="store.name" decorative />
                    </span>
                    <span class="sav-store-tile-divider" aria-hidden="true"></span>
                    <span class="sav-store-tile-store-meta">
                      <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                      <span class="sav-store-tile-count">{{ store.couponCountText }}</span>
                    </span>
                  </a>
                }
              </div>
            </div>
          </section>
        }

        @if (membershipSection().plans.length > 0) {
          <section class="sav-membership" aria-label="Membership plans">
            <header class="sav-membership-header">
              <div class="sav-membership-chip">
                <span aria-hidden="true"></span>
                Membership Plans
              </div>
              <h2>{{ membershipSection().heading }}</h2>
              @if (membershipSection().description) {
                <p>{{ membershipSection().description }}</p>
              }
            </header>

            <div class="sav-membership-grid">
              @for (plan of membershipSection().plans; track plan.id) {
                <article class="sav-plan-card" [class.sav-plan-card-featured]="plan.featured">
                  @if (plan.badge) {
                    <span class="sav-plan-card-badge">{{ plan.badge }}</span>
                  }
                  <div class="sav-plan-card-top">
                    <h3>{{ plan.name }}</h3>
                    @if (plan.description) {
                      <p>{{ plan.description }}</p>
                    }
                  </div>
                  @if (plan.price || plan.period) {
                    <p class="sav-plan-card-price">
                      @if (plan.price) {
                        <strong>{{ plan.price }}</strong>
                      }
                      @if (plan.period) {
                        <span>{{ plan.period }}</span>
                      }
                      <!-- The merchant's own discount label for the plan, e.g. "50% OFF" -->
                      @if (plan.discount) {
                        <em class="sav-plan-card-discount">{{ plan.discount }}</em>
                      }
                    </p>
                  }
                  @if (plan.features.length > 0) {
                    <ul>
                      @for (feature of plan.features; track feature.text) {
                        <li [class.is-muted]="feature.muted">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                            @if (feature.muted) {
                              <path
                                d="M6 18L18 6M6 6L18 18"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ></path>
                            } @else {
                              <path
                                d="M5 13L9 17L19 7"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ></path>
                            }
                          </svg>
                          {{ feature.text }}
                        </li>
                      }
                    </ul>
                  }
                  <!-- Leaves for the merchant through the store click-out (or the plan's own
                       affiliate link), in a new tab with the same rel the coupon and product
                       buttons carry. It used to be an inert <button>. -->
                  @if (plan.href) {
                    <a
                      class="sav-plan-btn"
                      [class.sav-plan-btn-featured]="plan.featured"
                      [class.sav-plan-btn-outline]="!plan.featured"
                      [href]="plan.href"
                      target="_blank"
                      rel="nofollow sponsored noopener"
                      [attr.aria-label]="plan.buttonText + ': ' + plan.name"
                    >
                      {{ plan.buttonText }}
                    </a>
                  } @else {
                    <button
                      type="button"
                      class="sav-plan-btn"
                      [class.sav-plan-btn-featured]="plan.featured"
                      [class.sav-plan-btn-outline]="!plan.featured"
                    >
                      {{ plan.buttonText }}
                    </button>
                  }
                  @if (plan.footnote) {
                    <p class="sav-plan-card-footnote">{{ plan.footnote }}</p>
                  }
                </article>
              }
            </div>
          </section>
        }

        @if (topSavingsProducts().length > 0) {
          <section
            class="sav-essentials"
            [attr.aria-label]="storeDisplayName() + ' biggest price drops'"
          >
            <header class="sav-essentials-head">
              <div>
                <h2>{{ topSavingsHeading }}</h2>
                <p>Ranked by how far the price has dropped</p>
              </div>
            </header>

            <div class="sav-product-grid">
              @for (product of topSavingsProducts(); track product.id) {
                <app-product-card
                  [title]="product.title"
                  [image]="product.imageUrl"
                  [hoverImage]="product.hoverImageUrl"
                  [price]="product.price"
                  [oldPrice]="product.oldPrice"
                  [href]="product.path"
                  [buyHref]="product.buyHref"
                  [productId]="product.id"
                />
              }
            </div>
          </section>
        }

        @if (storeProducts().length > 0) {
          <section class="sav-essentials" [attr.aria-label]="storeDisplayName() + ' products'">
            <header class="sav-essentials-head">
              <div>
                <h2>{{ productsHeading }}</h2>
                <p>Curated {{ storeDisplayName() }} products</p>
              </div>
            </header>

            <div class="sav-product-grid">
              @for (product of storeProducts(); track product.id) {
                <app-product-card
                  [title]="product.title"
                  [image]="product.imageUrl"
                  [hoverImage]="product.hoverImageUrl"
                  [price]="product.price"
                  [oldPrice]="product.oldPrice"
                  [href]="product.path"
                  [buyHref]="product.buyHref"
                  [productId]="product.id"
                />
              }
            </div>

            @if (hasMoreProducts()) {
              <div class="sav-load-more-wrap">
                <button
                  type="button"
                  class="sav-load-more-btn"
                  [disabled]="loadingMoreProducts()"
                  (click)="loadMoreProducts()"
                >
                  @if (loadingMoreProducts()) {
                    Loading…
                  } @else {
                    Load more products
                    <span class="sav-load-more-count"
                      >({{ storeProducts().length }} of {{ productsTotal() }})</span
                    >
                  }
                </button>
              </div>
            }

            <!--
              Real anchors underneath the Load more button, not instead of it.

              The button is a click handler, so a crawler can never fire it — which is why a store
              with 1,867 products exposed 24 of them and orphaned the rest. These links are in the
              server-rendered HTML, so the whole catalogue is walkable with JavaScript switched off,
              while the button keeps the existing behaviour for people.
            -->
            @if (productTotalPages() > 1) {
              <nav class="sav-product-pagination" aria-label="Product pages">
                @if (currentProductPage() > 1) {
                  <a
                    class="sav-product-pagestep"
                    [routerLink]="storeRouterLink()"
                    [queryParams]="productPageQuery(currentProductPage() - 1)"
                    rel="prev"
                    >← Previous</a
                  >
                }

                @for (page of productPageLinks(); track $index) {
                  @if (page === null) {
                    <span class="sav-product-pagegap" aria-hidden="true">…</span>
                  } @else if (page === currentProductPage()) {
                    <span class="sav-product-pagenum is-current" aria-current="page">{{ page }}</span>
                  } @else {
                    <a
                      class="sav-product-pagenum"
                      [routerLink]="storeRouterLink()"
                      [queryParams]="productPageQuery(page)"
                      >{{ page }}</a
                    >
                  }
                }

                @if (currentProductPage() < productTotalPages()) {
                  <a
                    class="sav-product-pagestep"
                    [routerLink]="storeRouterLink()"
                    [queryParams]="productPageQuery(currentProductPage() + 1)"
                    rel="next"
                    >Next →</a
                  >
                }
              </nav>
            }
          </section>
        }

        @if (partnerCoupons().length > 0) {
          <section class="sav-partner-coupons" aria-label="Partner offers and coupons">
            <header class="sav-partner-coupons-head">
              <div>
                <span>Member Perks</span>
                <h2>Partner Offers &amp; Coupons</h2>
                <p>Extra codes from partner brands, refreshed alongside this store's offers.</p>
              </div>
              <!--
                Not aria-hidden. These are real controls with real labels, and hiding the container from
                the accessibility tree while its buttons stayed keyboard-focusable meant a keyboard user
                could tab to a control no screen reader would announce.
              -->
              <div class="sav-partner-coupons-nav">
                <button type="button" aria-label="Previous partner offers">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 19L8 12L15 5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                </button>
                <button type="button" aria-label="Next partner offers">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 5L16 12L9 19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                </button>
              </div>
            </header>

            <div class="sav-partner-coupons-grid">
              @for (coupon of partnerCoupons(); track coupon.id) {
                <!-- `ctaHref` is that partner's real offer URL, so the CTA stays middle-clickable;
                     the handler then does the same two-tab open as the store's own coupons. -->
                <app-offer-card
                  [title]="coupon.title"
                  [storeName]="coupon.storeName"
                  [logoUrl]="coupon.storeLogoUrl"
                  [storePath]="coupon.path"
                  [expires]="coupon.expires"
                  [ctaType]="coupon.isDeal ? 'deal' : 'code'"
                  [ctaHref]="coupon.path + '/coupon/' + coupon.id"
                  (action)="openPartnerOffer(coupon, $event)"
                />
              }
            </div>
          </section>
        }

        <!-- Hidden entirely when there is nothing to list: the heading alone read as a broken
             section on stores whose category has no guides. -->
        @if (storeBlogs().length > 0) {
          <section class="sav-shopping-guides mb-[50px]" aria-label="Smart shopping guides">
            <div class="sav-site-container">
              <app-section-heading
                [heading]="storeDisplayName() + ' Shopping Guides, Coupon Tips and Deal Updates'"
                [subtitle]="
                  'Expert savings advice, verified coupon strategies and deal updates for ' +
                  storeDisplayName() +
                  ' shoppers.'
                "
              />

              @if (storeBlogs().length > 0) {
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-20">
                  @for (blog of storeBlogs(); track blog.id) {
                    <article class="group cursor-pointer">
                      <a
                        class="relative overflow-hidden rounded-[2.5rem] aspect-[5/4] mb-8 shadow-sm block"
                        [routerLink]="blog.path"
                      >
                        <img
                          [src]="blog.imageUrl"
                          [alt]="blog.imageAlt"
                          class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        @if (blog.storeLogoUrl) {
                          <span
                            class="absolute bottom-4 right-4 w-14 h-14 rounded-full bg-white shadow-lg overflow-hidden ring-1 ring-black/5"
                          >
                            <img
                              [src]="blog.storeLogoUrl"
                              alt="Store logo"
                              class="w-full h-full object-cover rounded-full"
                            />
                          </span>
                        }
                      </a>
                      <div
                        class="flex items-center gap-3 text-[10px] font-black text-[var(--color-primary)] uppercase tracking-widest mb-4"
                      >
                        <a
                          class="hover:text-[var(--color-primary-deep)] transition-colors"
                          [routerLink]="blog.categoryPath"
                          >{{ blog.categoryName }}</a
                        >
                        <span class="w-1 h-1 bg-slate-300 rounded-full"></span>
                        <span class="text-slate-400">{{ blog.readTime }}</span>
                        <span class="w-1 h-1 bg-slate-300 rounded-full"></span>
                        <a
                          class="text-slate-500 hover:text-[var(--color-primary-deep)] transition-colors"
                          [routerLink]="blog.authorPath"
                          >{{ blog.authorName }}</a
                        >
                      </div>
                      <h3 class="group-hover:text-[var(--color-primary)] transition-colors mb-4">
                        <a [routerLink]="blog.path">{{ blog.title }}</a>
                      </h3>
                      <p class="text-slate-500 leading-relaxed line-clamp-3 font-medium">
                        {{ blog.excerpt }}
                      </p>
                    </article>
                  }
                </div>
              } @else {
                <div
                  class="rounded-[2rem] border border-slate-100 bg-white p-8 text-center shadow-sm mb-20"
                >
                  <p class="text-slate-500 font-medium">
                    No assigned {{ storeDisplayName() }} shopping guides are available yet.
                  </p>
                </div>
              }
            </div>
          </section>
        }

        @if (faqItems().length > 0) {
          <section class="sav-faq" aria-label="Frequently asked questions">
            <header class="sav-faq-head">
              <h2>FAQ's</h2>
              <p>Everything shoppers ask before using {{ storeDisplayName() }} offers.</p>
            </header>

            <div class="sav-store-faq-list">
              @for (faq of faqItems(); track faq.question; let isFirst = $first) {
                <details class="sav-store-faq-item" [open]="isFirst">
                  <summary>{{ faq.question }}</summary>
                  <p>{{ faq.answer }}</p>
                </details>
              }
            </div>
          </section>
        }

        <section class="sav-longform" aria-label="Complete savings guide">
          <header class="sav-longform-head">
            <h2>{{ longformHeading }}</h2>
            <p>{{ longformIntroText }}</p>
          </header>

          <!--
            Expand and collapse with no JavaScript, the same device the coupon card uses: the
            checkbox holds the state and CSS lifts the clamp off its sibling.

            The guide is printed IN FULL either way. Before this it was replaced by a 500 character
            plain-text preview until someone clicked, and the content element was absent from the
            served markup altogether — on /faphouse-coupon-codes that put 503 of the guide's 6,607
            characters on the page and hid the rest from crawlers.
          -->
          <input
            type="checkbox"
            class="sav-readmore-input"
            id="sav-longform-readmore"
            aria-controls="sav-longform-readmore-body"
            aria-label="Show the full savings guide"
          />
          <div
            id="sav-longform-readmore-body"
            class="sav-longform-content sav-readmore-rich"
            [class.is-clamped]="shouldShowLongformToggle"
            [innerHTML]="longformHtml | trustedCmsHtml"
          ></div>

          @if (shouldShowLongformToggle) {
            <label class="sav-longform-toggle" for="sav-longform-readmore">
              <span class="sav-readmore-more">Read More</span>
              <span class="sav-readmore-less">Read Less</span>
              <span class="sav-longform-toggle-icon" aria-hidden="true">+</span>
            </label>
          }
        </section>

        <section class="sav-reviews" aria-label="Shopper reviews">
          <header class="sav-reviews-head">
            <div class="sav-reviews-title-wrap">
              <div class="sav-reviews-eyebrow">
                <span aria-hidden="true"></span>
                <p>Social Proof</p>
              </div>
              <h2>What shoppers say.</h2>
              <p>Ratings and written reviews from shoppers who used these offers.</p>
            </div>

            @if (reviewsCount() > 0) {
              <div class="sav-reviews-controls">
                <div class="sav-reviews-score">
                  <div class="sav-score-stars" aria-hidden="true">
                    @for (s of reviewRatingStars; track s) {
                      <svg
                        width="18"
                        height="17"
                        viewBox="0 0 18 17"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g fill="none" fill-rule="evenodd">
                          <g transform="translate(-273 -4323)">
                            <g transform="translate(100 4266)">
                              <g transform="translate(170 54)">
                                <g>
                                  <polygon
                                    fill-rule="nonzero"
                                    points="0 0 24 0 24 24 0 24"
                                  ></polygon>
                                  <path
                                    d="M12 17.27L16.15 19.78C16.91 20.24 17.84 19.56 17.64 18.7L16.54 13.98 20.21 10.8C20.88 10.22 20.52 9.12 19.64 9.05L14.81 8.64 12.92 4.18C12.58 3.37 11.42 3.37 11.08 4.18L9.19 8.63 4.36 9.04C3.48 9.11 3.12 10.21 3.79 10.79L7.46 13.97 6.36 18.69C6.16 19.55 7.09 20.23 7.85 19.77L12 17.27Z"
                                    [attr.fill]="s <= reviewsScoreStars() ? '#F5B61A' : '#D9D9D9'"
                                  ></path>
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                    }
                  </div>
                  <h3>
                    {{ reviewsAverage() }} / 5.0
                    <small
                      >({{ reviewsCount() }}
                      {{ reviewsCount() === 1 ? 'Review' : 'Reviews' }})</small
                    >
                  </h3>
                </div>
              </div>
            }
          </header>

          @if (reviewItems().length > 0) {
            <div class="sav-reviews-track">
              @for (review of reviewItems(); track review.id) {
                <article class="sav-review-card">
                  <div class="sav-review-card-top">
                    <div class="sav-review-stars" aria-hidden="true">
                      @for (s of review.stars; track s) {
                        <svg
                          width="18"
                          height="17"
                          viewBox="0 0 18 17"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g fill="none" fill-rule="evenodd">
                            <g transform="translate(-273 -4323)">
                              <g transform="translate(100 4266)">
                                <g transform="translate(170 54)">
                                  <g>
                                    <polygon
                                      fill-rule="nonzero"
                                      points="0 0 24 0 24 24 0 24"
                                    ></polygon>
                                    <path
                                      d="M12 17.27L16.15 19.78C16.91 20.24 17.84 19.56 17.64 18.7L16.54 13.98 20.21 10.8C20.88 10.22 20.52 9.12 19.64 9.05L14.81 8.64 12.92 4.18C12.58 3.37 11.42 3.37 11.08 4.18L9.19 8.63 4.36 9.04C3.48 9.11 3.12 10.21 3.79 10.79L7.46 13.97 6.36 18.69C6.16 19.55 7.09 20.23 7.85 19.77L12 17.27Z"
                                      [attr.fill]="s <= review.rating ? '#F5B61A' : '#D9D9D9'"
                                    ></path>
                                  </g>
                                </g>
                              </g>
                            </g>
                          </g>
                        </svg>
                      }
                    </div>
                    <h4>{{ review.title }}</h4>
                  </div>
                  <p>{{ review.text }}</p>
                  <div class="sav-review-card-foot">
                    <div class="sav-review-card-user">
                      <span aria-hidden="true">{{ review.initials }}</span>
                      <div>
                        <strong>{{ review.name }}</strong>
                        @if (review.designation) {
                          <small>{{ review.designation }}</small>
                        }
                      </div>
                    </div>
                    @if (review.dateLabel) {
                      <p>{{ review.dateLabel }}</p>
                    }
                  </div>
                </article>
              }
            </div>
          } @else {
            <p class="sav-reviews-empty">No reviews yet — be the first to review this store.</p>
          }

          <div class="sav-reviews-cta">
            @if (reviewSubmitted()) {
              <p class="sav-review-thanks">
                Thanks! Your review has been submitted and will appear once our team approves it.
              </p>
            } @else {
              <button type="button" (click)="toggleReview()">Write a Review</button>
            }
          </div>

          @if (isReviewOpen()) {
            <div
              class="sav-review-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Leave a review"
              tabindex="-1"
              (click)="onReviewBackdrop($event)"
              (keydown.escape)="closeReview()"
            >
              <div class="sav-review-modal-card">
                <button
                  type="button"
                  class="sav-review-modal-close"
                  (click)="closeReview()"
                  aria-label="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>

                @if (reviewSubmitted()) {
                  <div class="sav-review-modal-success">
                    <span class="sav-review-modal-tick" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <h3>Thanks for your review!</h3>
                    <p>It will appear on this page once our team approves it.</p>
                    <button type="button" class="sav-review-modal-done" (click)="closeReview()">
                      Close
                    </button>
                  </div>
                } @else {
                  <h3 class="sav-review-modal-title">Leave a review</h3>
                  <form
                    class="sav-review-form"
                    [formGroup]="reviewForm"
                    (ngSubmit)="submitReview()"
                    novalidate
                  >
                    <div class="sav-honeypot" aria-hidden="true">
                      <label
                        >Company website<input
                          formControlName="companyWebsite"
                          type="text"
                          tabindex="-1"
                          autocomplete="off"
                      /></label>
                    </div>

                    <div class="sav-review-form-rating">
                      <span class="sav-review-form-label">Your rating</span>
                      <div class="sav-review-form-stars" role="group" aria-label="Choose a rating">
                        @for (s of reviewRatingStars; track s) {
                          <button
                            type="button"
                            class="sav-review-star-btn"
                            (click)="setReviewRating(s)"
                            (mouseenter)="setReviewHover(s)"
                            (mouseleave)="clearReviewHover()"
                            [attr.aria-label]="s + (s === 1 ? ' star' : ' stars')"
                          >
                            <svg
                              width="24"
                              height="23"
                              viewBox="0 0 18 17"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g fill="none" fill-rule="evenodd">
                                <g transform="translate(-273 -4323)">
                                  <g transform="translate(100 4266)">
                                    <g transform="translate(170 54)">
                                      <g>
                                        <polygon
                                          fill-rule="nonzero"
                                          points="0 0 24 0 24 24 0 24"
                                        ></polygon>
                                        <path
                                          d="M12 17.27L16.15 19.78C16.91 20.24 17.84 19.56 17.64 18.7L16.54 13.98 20.21 10.8C20.88 10.22 20.52 9.12 19.64 9.05L14.81 8.64 12.92 4.18C12.58 3.37 11.42 3.37 11.08 4.18L9.19 8.63 4.36 9.04C3.48 9.11 3.12 10.21 3.79 10.79L7.46 13.97 6.36 18.69C6.16 19.55 7.09 20.23 7.85 19.77L12 17.27Z"
                                          [attr.fill]="
                                            s <= reviewActiveStars() ? '#F5B61A' : '#D9D9D9'
                                          "
                                        ></path>
                                      </g>
                                    </g>
                                  </g>
                                </g>
                              </g>
                            </svg>
                          </button>
                        }
                      </div>
                    </div>

                    <div class="sav-review-form-grid">
                      <label class="sav-review-field">
                        <span>Your name <span class="req">*</span></span>
                        <input
                          formControlName="userName"
                          type="text"
                          [class.is-invalid]="isReviewInvalid('userName')"
                        />
                      </label>
                      <label class="sav-review-field">
                        <span>Designation</span>
                        <input
                          formControlName="userDesignation"
                          type="text"
                          placeholder="e.g. Verified Shopper"
                        />
                      </label>
                    </div>

                    <label class="sav-review-field">
                      <span>Review title <span class="req">*</span></span>
                      <input
                        formControlName="title"
                        type="text"
                        [class.is-invalid]="isReviewInvalid('title')"
                      />
                    </label>

                    <label class="sav-review-field">
                      <span>Your review <span class="req">*</span></span>
                      <textarea
                        formControlName="description"
                        rows="4"
                        [class.is-invalid]="isReviewInvalid('description')"
                      ></textarea>
                    </label>

                    @if (reviewError()) {
                      <p class="sav-review-error">{{ reviewError() }}</p>
                    }

                    <div class="sav-review-form-actions">
                      <button type="submit" [disabled]="reviewSubmitting()">
                        {{ reviewSubmitting() ? 'Submitting…' : 'Submit review' }}
                      </button>
                      <button type="button" class="is-ghost" (click)="closeReview()">Cancel</button>
                    </div>
                  </form>
                }
              </div>
            </div>
          }
        </section>
      </div>
    </div>
  </section>
} @else {
  <section class="sav-store-empty" aria-label="Store unavailable">
    <h1>Store details are unavailable.</h1>
    <p>Please try again later.</p>
  </section>
}

@if (activeOffer(); as offer) {
  <app-offer-modal [offer]="offer" (closed)="closeOffer()" (codeCopied)="recordCouponHit($event)" />
}
